import { FunctionalComponent } from 'preact'

/** Generic column item for the column selector modal */
export interface ColumnSelectorItem {
  id: string
  label: string
  visible: boolean
  required?: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  columns: ColumnSelectorItem[]
  onToggle: (columnId: string) => void
  onReset: () => void
}

export const ColumnSelectorModal: FunctionalComponent<Props> = ({
  isOpen,
  onClose,
  columns,
  onToggle,
  onReset
}) => {
  if (!isOpen) return null

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Select Columns
          </h2>
          <button
            onClick={onClose}
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-6 space-y-3 max-h-96 overflow-y-auto">
          {columns.map(col => (
            <label
              key={col.id}
              class={`flex items-center gap-3 p-3 rounded-lg border ${
                col.required
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 cursor-not-allowed opacity-60'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={col.visible}
                disabled={col.required}
                onChange={() => !col.required && onToggle(col.id)}
                class="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 disabled:cursor-not-allowed"
              />
              <div class="flex-1">
                <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {col.label}
                  {col.required && (
                    <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">(Required)</span>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div class="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onReset}
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Reset to Defaults
          </button>
          <button
            onClick={onClose}
            class="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
